import {
  PinComponent,
  PinComponentOfType,
  PinComponentType,
  PinThemePinComponentSettings,
  PinThemePinComponentSettingsSchema,
  PinThemePinComponentSettingsType,
  TextEditorHandler,
  TypographyRecord,
  TypographySettings,
} from '@lessonup/pins-shared';
import { UnionToTuple } from '@lessonup/utils';

export type PinComponentSettingsHandlerArgsMapper = {
  TEXT: {
    textEditorHandler?: TextEditorHandler;
    isTextEditMode?: boolean;
  };
  DROP_ZONE: {
    textEditorHandler?: TextEditorHandler;
    isTextEditMode?: boolean;
  };
};

export type PinThemePinComponentSettingsHandlerParams<T extends PinComponentType> =
  T extends keyof PinComponentSettingsHandlerArgsMapper
    ? {
        settings: PinComponentOfType<T>['settings'];
      } & PinComponentSettingsHandlerArgsMapper[T]
    : {
        settings: PinComponentOfType<T>['settings'];
      };

export type PinThemePinComponentSettingsHandlers = {
  [T in PinThemePinComponentSettingsType]: (
    params: PinThemePinComponentSettingsHandlerParams<T>
  ) => PinComponentOfType<T>['settings'];
};

export type PinThemePinComponentSchemaReturnTypes = {
  [K in keyof PinThemePinComponentSettings]: UnionToTuple<keyof PinThemePinComponentSettings[K]> extends [string] // will return [string] if object schema is empty
    ? never[]
    : UnionToTuple<keyof PinThemePinComponentSettings[K]>;
};

export const pinThemePinComponentSchemaReturnTypes = PinThemePinComponentSettingsSchema.keyof().options.reduce(
  (acc, key) => ({
    ...acc,
    [key]: PinThemePinComponentSettingsSchema.shape[key].keyof().options,
  }),
  {} as PinThemePinComponentSchemaReturnTypes
);

export function getResetablePinComponentSettings(pinComponent: PinComponent) {
  return pinThemePinComponentSchemaReturnTypes[pinComponent.type];
}

type HasSetting<T, P> = T extends { settings: P } ? T : never;
type HasTypography<T> = HasSetting<T, { typographyKey?: string }>;

export function getTypography<P extends HasTypography<PinComponent>>(
  pinComponentSettings: P['settings'],
  typographyRecord?: TypographyRecord
): TypographySettings | undefined {
  return pinComponentSettings.typographyKey
    ? typographyRecord?.[pinComponentSettings.typographyKey]
    : typographyRecord?.normalText;
}
